(() => {
  if (window.SBSCampaigns) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character]));
  function deadline(campaign, now) {
    const hardEnd = campaign.end ? Date.parse(campaign.end) : null;
    if (campaign.timerMode === 'Evergreen') {
      const duration = Number(campaign.evergreenDays) * 86400000;
      const anchor = campaign.start ? Date.parse(campaign.start) : 0;
      if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(anchor)) return NaN;
      const end = anchor + (Math.floor((now - anchor) / duration) + 1) * duration;
      return hardEnd === null ? end : Math.min(end, hardEnd);
    }
    return hardEnd;
  }
  function select(config, types, variant, cart, now = Date.now()) {
    const results = [];
    const selectedTypes = new Map();
    const campaigns = [...config.campaigns].sort((a, b) => Number(a.priority) - Number(b.priority) || a.handle.localeCompare(b.handle));
    for (const campaign of campaigns) {
      if (!types.includes(campaign.type)) continue;
      if (campaign.countries?.length && !campaign.countries.some(country => country.trim().toUpperCase() === config.country)) continue;
      const start = campaign.start ? Date.parse(campaign.start) : null;
      const hardEnd = campaign.end ? Date.parse(campaign.end) : null;
      if (start !== null && (!Number.isFinite(start) || now < start)) continue;
      if (hardEnd !== null && (!Number.isFinite(hardEnd) || now >= hardEnd)) continue;
      if (!variant && ['sale', 'discount-code'].includes(campaign.type)) continue;
      if (campaign.requireAvailable && !variant?.available) continue;
      const discounted = variant && Number(variant.compare_at_price) > Number(variant.price);
      if (campaign.productState === 'Discounted' && !discounted) continue;
      if (campaign.productState === 'Full price' && (!variant || discounted)) continue;
      if (campaign.type === 'discount-code' && (!campaign.code || Number(campaign.percent) <= 0)) continue;
      const minimum = Number(campaign.minimum) * 100;
      if (minimum > 0 && campaign.currency !== config.currency) continue;
      let amount = Number(variant?.price) || 0;
      if (campaign.scope === 'Cart total') amount = cart?.total_price || 0;
      if (campaign.scope === 'Matching collection total') amount = Number(campaign.matchingCartTotal) || 0;
      if (amount < minimum) continue;
      const end = deadline(campaign, now);
      if (campaign.timerMode !== 'None' && (!Number.isFinite(end) || end <= now)) continue;
      const previous = selectedTypes.get(campaign.type);
      if (previous && (!previous.every(entry => entry.allowMultiple) || !campaign.allowMultiple)) continue;
      selectedTypes.set(campaign.type, [...(previous || []), campaign]);
      results.push({campaign, end});
    }
    return results;
  }
  function timerText(end, now = Date.now()) {
    const remaining = Math.max(0, end - now);
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor(remaining / 3600000) % 24;
    const minutes = Math.floor(remaining / 60000) % 60;
    const seconds = Math.floor(remaining / 1000) % 60;
    return [days ? `${days}d` : '', `${String(hours).padStart(2, '0')}h`, `${String(minutes).padStart(2, '0')}m`, remaining < 86400000 ? `${String(seconds).padStart(2, '0')}s` : ''].filter(Boolean).join(' ');
  }
  function message(campaign, variant) {
    const amount = campaign.variantAmounts?.[variant?.id] || '';
    return campaign.html.replaceAll('{discount amount}', escape(amount)).replaceAll('{discount code}', escape(campaign.code)).replaceAll('{discount percentage}', escape(campaign.percent) + '%');
  }
  window.SBSCampaigns = {select, deadline, timerText, message};
  if (!customElements.get('sbs-campaign-messages')) {
    customElements.define('sbs-campaign-messages', class extends HTMLElement {
      connectedCallback() {
        if (this.timer) return;
        this.main = this.closest('.cc-main-product');
        const data = this.main?.querySelector('[data-pdp-delivery-config]');
        if (!data) return;
        this.config = JSON.parse(data.textContent);
        this.variant = this.config.variants.find(variant => String(variant.id) === String(this.config.selectedVariantId));
        this.types = (this.dataset.types || 'notice').split(',');
        this.variantChange = event => { this.variant = event.detail?.variant || null; this.update(); };
        this.cartChange = event => { this.cart = event.detail?.cart; this.refresh(); };
        this.main.addEventListener('on:variant:change', this.variantChange);
        for (const name of ['on:cart:add', 'on:line-item:change']) document.addEventListener(name, this.cartChange);
        this.update();
        this.readCart();
        this.timer = setInterval(() => this.update(), 1000);
      }
      disconnectedCallback() {
        clearInterval(this.timer);
        this.timer = null;
        this.main?.removeEventListener('on:variant:change', this.variantChange);
        for (const name of ['on:cart:add', 'on:line-item:change']) document.removeEventListener(name, this.cartChange);
      }
      async readCart() {
        if (!this.config.campaigns.some(campaign => this.types.includes(campaign.type) && campaign.scope === 'Cart total')) return;
        try {
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
          if (!response.ok) return;
          this.cart = await response.json();
          this.update();
        } catch {}
      }
      async refresh() {
        this.update();
        if (!this.config.campaigns.some(campaign => this.types.includes(campaign.type) && campaign.scope === 'Matching collection total')) return;
        if (this.refreshPending) return;
        this.refreshPending = true;
        try {
          const url = new URL(location.href);
          url.searchParams.set('sections', this.config.sectionId);
          const response = await fetch(url);
          if (!response.ok) return;
          const result = await response.json();
          const doc = new DOMParser().parseFromString(result[this.config.sectionId], 'text/html');
          const data = doc.querySelector('[data-pdp-delivery-config]');
          if (data) this.config.campaigns = JSON.parse(data.textContent).campaigns;
          this.update();
        } catch {} finally { this.refreshPending = false; }
      }
      update() {
        const selected = select(this.config, this.types, this.variant, this.cart);
        const signature = JSON.stringify(selected.map(({campaign, end}) => [campaign.handle, campaign.html, this.variant?.id, campaign.variantAmounts?.[this.variant?.id], end]));
        if (signature !== this.signature) {
          this.signature = signature;
          this.innerHTML = selected.map(({campaign, end}) => {
            const timer = campaign.timerMode !== 'None' ? `<strong class="sbs-campaign__timer" data-campaign-end="${end}"></strong>` : '';
            const html = message(campaign, this.variant);
            const content = html.includes('{timer}') ? html.replaceAll('{timer}', timer) : html + (timer ? `<div class="sbs-campaign__countdown">${escape(this.config.endsLabel)} ${timer}</div>` : '');
            return `<div class="sbs-campaign sbs-campaign--${escape(campaign.type)} campaign-message rte" data-campaign-message="${escape(campaign.handle)}">${content}</div>`;
          }).join('');
        }
        this.hidden = !selected.length;
        this.querySelectorAll('[data-campaign-end]').forEach(timer => { timer.textContent = timerText(Number(timer.dataset.campaignEnd)); });
      }
    });
  }
})();
